import { AnalyticsEventTopic } from '~/modules/analytics/analyticsHelper';
import { ElementType } from '~/modules/analytics/gtmEventTypes/constants';
import { GA4ContentClickEvent, GTMBaseEvent, GtmEventType } from './gtmEventType';

const ORG_PICKER_ELEMENT_TYPES: ReadonlySet<string> = new Set([
    ElementType.ORG_PICKER_BADGE,
    ElementType.ORG_PICKER_DROPDOWN,
    ElementType.ORG_PICKER_SWITCH_ORG,
    ElementType.ORG_PICKER_SWITCH_SUCCESS,
    ElementType.ORG_PICKER_DISMISS,
    ElementType.ORG_PICKER_NO_RESULTS,
    ElementType.ORG_PICKER_SEARCH_USED,
    ElementType.ORG_PICKER_FILTER_USED,
    ElementType.ORG_PICKER_ERROR,
]);

// Generic, non-identifying UI copy per element type. The selected org's own name/edition is
// deliberately kept out of every org picker event — that's customer business data and shouldn't
// reach GTM/GA4. The search-used event likewise carries no keyword text (the typed term can hold
// org names/usernames), only that a search occurred.
const CLICK_TEXT_BY_ELEMENT_TYPE: Record<string, string> = {
    [ElementType.ORG_PICKER_BADGE]: 'org selector',
    [ElementType.ORG_PICKER_DROPDOWN]: 'org menu',
    [ElementType.ORG_PICKER_SWITCH_ORG]: 'switch org',
    [ElementType.ORG_PICKER_SWITCH_SUCCESS]: 'org switched',
    [ElementType.ORG_PICKER_DISMISS]: 'org menu dismissed',
    [ElementType.ORG_PICKER_NO_RESULTS]: 'no matching orgs',
    [ElementType.ORG_PICKER_SEARCH_USED]: 'org search used',
    [ElementType.ORG_PICKER_FILTER_USED]: 'org filter used',
    [ElementType.ORG_PICKER_ERROR]: 'org load failed',
};

// GA4 event name for org picker load failures. Deliberately distinct from custEv_appEngagement
// (below) so error volume never inflates the engagement/CTR aggregates keyed on that name — an
// error is not an engagement. Keep these separate: consolidating them back onto one event name
// silently poisons those dashboards.
const ORG_PICKER_ERROR_EVENT = 'custEv_orgPickerError';

export class GtmOrgPickerClickType extends GtmEventType {
    canHandle(event: GTMBaseEvent): boolean {
        return event.topic === AnalyticsEventTopic.ANALYTICS_CLICK && ORG_PICKER_ELEMENT_TYPES.has(event.elementType);
    }

    override getBody(event: GTMBaseEvent): GA4ContentClickEvent {
        // Load failures ride a dedicated event name (see ORG_PICKER_ERROR_EVENT) and carry which
        // loader failed in error_source rather than overloading content_category, so error volume
        // stays out of the engagement aggregates and is filterable on its own field.
        if (event.elementType === ElementType.ORG_PICKER_ERROR) {
            const body: GA4ContentClickEvent = {
                event: ORG_PICKER_ERROR_EVENT,
                element_type: event.elementType,
                click_text: CLICK_TEXT_BY_ELEMENT_TYPE[event.elementType],
            };
            if (typeof event.error_source === 'string') {
                body.error_source = event.error_source;
            }
            return body;
        }
        const body: GA4ContentClickEvent = {
            event: 'custEv_appEngagement',
            element_type: event.elementType,
            click_text: CLICK_TEXT_BY_ELEMENT_TYPE[event.elementType],
        };
        // Switch success fires on two different paths that look identical otherwise: a silent
        // cookie-based reselect on page load, and a real click after the user picks an org.
        // GTM can't tell them apart without this — an inflated "switch" count would otherwise
        // count page loads as user interactions.
        if (event.elementType === ElementType.ORG_PICKER_SWITCH_SUCCESS) {
            body.content_category = event.autoReselected ? 'auto reselected' : 'manual selection';
        }
        // "Switch org" also renders in two places with an identical click otherwise: the mobile
        // org card (header.ts) and the desktop dropdown (orgContextMenu.ts). Surfacing which one
        // fired lets GTM tell a mobile drop-off from a desktop one instead of lumping them together.
        if (event.elementType === ElementType.ORG_PICKER_SWITCH_ORG && typeof event.surface === 'string') {
            body.content_category = event.surface;
        }
        // The filter value is a generic edition bucket the user chose (all/production/sandbox/…),
        // not the selected org's own edition — so it's safe to surface the same way SWITCH_ORG
        // surfaces 'mobile'/'desktop'. It tells GTM which filter bucket users reach for without
        // exposing any org's name/edition/username.
        if (event.elementType === ElementType.ORG_PICKER_FILTER_USED && typeof event.edition === 'string') {
            body.content_category = event.edition;
        }
        return body;
    }
}
